import React, { Suspense, useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { Dialog, DialogContent } from '@material-ui/core';

import retryImport from 'app/shared/helpers/retryImport';
import lazyWithReload from 'app/shared/helpers/lazyWithReload';
import { CloseIconButton } from 'app/components/CloseIconButton';
import { Loader } from 'app/components/Loader';
import { DateControlDialogProps } from './DateControlDialog.interfaces';
import { useStyles } from './DateControlDialog.styles';
import { useDateControlDialog } from './useDateControlDialog';
import { FieldsEnum } from 'app/shared/enums';

const Calendar = lazyWithReload(() =>
  retryImport(() => import('app/components/Calendar'))
);

const CenturyYearsView = ({
  onDecadeSelect,
  minDate,
  maxDate,
}: {
  onDecadeSelect: (decadeStart: number) => void;
  minDate: Date;
  maxDate: Date;
}) => {
  const classes = useStyles();

  const generateYearsGroupedByDecades = () => {
    const minYear = new Date(minDate).getFullYear();
    const maxYear = new Date(maxDate).getFullYear();

    const decades = new Map<number, number[]>();

    for (let year = minYear; year <= maxYear; year++) {
      const decadeStart = Math.floor((year - 1) / 10) * 10 + 1;
      if (!decades.has(decadeStart)) {
        decades.set(decadeStart, []);
      }
      decades.get(decadeStart)!.push(year);
    }

    return Array.from(decades.entries()).map(([decadeStart, years]) => ({
      decadeStart,
      decadeEnd: decadeStart + 9,
      decadeLabel: `${decadeStart}-${decadeStart + 9}`,
      years: years.sort((a, b) => a - b),
    }));
  };

  const decadeGroups = generateYearsGroupedByDecades();

  const minYear = new Date(minDate).getFullYear();
  const maxYear = new Date(maxDate).getFullYear();

  const minDecadeStart = Math.floor((minYear - 1) / 10) * 10 + 1;
  const maxDecadeStart = Math.floor((maxYear - 1) / 10) * 10 + 1;
  const maxDecadeEnd = maxDecadeStart + 9;

  const title = `${minDecadeStart} - ${maxDecadeEnd}`;

  return (
    <div className={classes.timePickerView}>
      <div className={classes.timePickerTitle}>
        <h3>{title}</h3>
      </div>
      <div className={classes.timePickerContainer}>
        {decadeGroups.map((group) => (
          <div
            onClick={() => onDecadeSelect(group.decadeStart)}
            key={group.decadeStart}
            className={classes.timePickerItem}
          >
            <span className={classes.timePickerText}>{group.decadeLabel}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const YearsView = ({
  onYearSelect,
  minDate,
  maxDate,
}: {
  onYearSelect: (year: number) => void;
  minDate: Date;
  maxDate: Date;
}) => {
  const classes = useStyles();

  const generateYearsList = () => {
    const minYear = new Date(minDate).getFullYear();
    const maxYear = new Date(maxDate).getFullYear();
    const years = [];

    for (let year = minYear; year <= maxYear; year++) {
      years.push(year);
    }

    return years.sort((a, b) => a - b); // Sort from oldest to newest
  };

  const years = generateYearsList();
  const minYear = new Date(minDate).getFullYear();
  const maxYear = new Date(maxDate).getFullYear();
  const title = `${minYear} - ${maxYear}`;

  return (
    <div className={classes.timePickerView}>
      <div className={classes.timePickerTitle}>
        <h3>{title}</h3>
      </div>
      <div className={classes.timePickerContainer}>
        {years.map((year) => (
          <div
            key={year}
            onClick={() => onYearSelect(year)}
            className={classes.timePickerItem}
          >
            <span className={classes.timePickerText}>{year}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DateControlDialog = (props: DateControlDialogProps) => {
  const {
    open,
    isExtended,
    datePickerViewType,
    handleClose,
    calendarChange,
    getCalendarValue,
    fieldId,
    disableCenturyView,
  } = props;

  const [currentView, setCurrentView] = useState<string | undefined>(
    datePickerViewType
  );

  const [selectedDecade, setSelectedDecade] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const {
    getDayName,
    getCalendarMinDate,
    getCalendarMaxDate,
    getCalendarActiveStartDate,
    getMonthYear,
    getShortWeekday,
    getMonthName,
  } = useDateControlDialog(props);

  const classes = useStyles();

  useEffect(() => {
    if (open) {
      setCurrentView(datePickerViewType);
      setSelectedDecade(null);
      setSelectedYear(null);

      if (datePickerViewType === 'decade') {
        setTimeout(() => {
          disableCenturyViewIfNeeded({ view: 'decade' });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, datePickerViewType, fieldId]);

  const handleDecadeSelect = useCallback((decadeStart: number) => {
    setSelectedDecade(decadeStart);
    setCurrentView('calendar');
  }, []);

  const handleBackToCentury = useCallback(() => {
    setCurrentView('century');
    setSelectedDecade(null);
  }, []);

  const handleBackToYears = useCallback(() => {
    setCurrentView('year');
    setSelectedYear(null);
  }, []);

  const handleYearSelect = useCallback((year: number) => {
    setSelectedYear(year);
    setCurrentView('calendar');
  }, []);

  const getCalendarViewType = () => {
    if (currentView === 'calendar' && selectedDecade) {
      return 'decade';
    }
    if (currentView === 'calendar' && selectedYear) {
      return 'year';
    }
    return datePickerViewType === 'century' ? 'decade' : datePickerViewType;
  };

  const getCalendarActiveStartDateForDecade = () => {
    if (selectedDecade) {
      return new Date(selectedDecade, 0, 1);
    }
    if (selectedYear) {
      return new Date(selectedYear, 0, 1);
    }
    return getCalendarActiveStartDate();
  };

  const fourNextDays = () => (
    <>
      {Array.from(Array(7).keys()).map((count) => {
        const day = moment().add(count, 'days').toDate();
        return (
          <div
            key={count}
            className={classes.dayNameButton}
            onClick={() => calendarChange(day)}
          >
            {getDayName(count, day)}
          </div>
        );
      })}
    </>
  );

  const handleDrillUp = ({ view }: { view: string }) => {
    if (view === 'century') {
      handleBackToCentury();
    } else if (selectedYear && view === 'decade') {
      handleBackToYears();
    }
  };

  const disableCenturyViewIfNeeded = ({ view }: { view: string }) => {
    const label = document.querySelector<HTMLElement>(
      '.react-calendar__navigation__label'
    );

    if (!label) return;

    if (disableCenturyView && view === 'decade') {
      label.style.pointerEvents = 'none';
    } else {
      label.style.pointerEvents = 'auto';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <div>
        {isExtended && (
          <div className={classes.navigationContainer}>{fourNextDays}</div>
        )}
        <div>
          <div className={classes.dialogTitle}>
            <CloseIconButton
              className={classes.dialogClose}
              onClick={handleClose}
            />
          </div>
          <DialogContent className={classes.dialogContent}>
            {currentView === 'century' ? (
              <CenturyYearsView
                onDecadeSelect={handleDecadeSelect}
                minDate={getCalendarMinDate()}
                maxDate={getCalendarMaxDate()}
              />
            ) : currentView === 'year' &&
              fieldId === FieldsEnum.AdditionalDrivers1BirthDate ? (
              <YearsView
                onYearSelect={handleYearSelect}
                minDate={getCalendarMinDate()}
                maxDate={getCalendarMaxDate()}
              />
            ) : (
              <Suspense fallback={<Loader className={classes.loader} />}>
                <Calendar
                  onChange={calendarChange}
                  value={getCalendarValue()}
                  minDate={getCalendarMinDate()}
                  maxDate={getCalendarMaxDate()}
                  defaultView={getCalendarViewType()}
                  defaultActiveStartDate={getCalendarActiveStartDateForDecade()}
                  locale={'pl-PL'}
                  formatMonthYear={getMonthYear}
                  formatShortWeekday={getShortWeekday}
                  formatMonth={getMonthName}
                  onDrillUp={handleDrillUp}
                  onActiveStartDateChange={disableCenturyViewIfNeeded}
                />
              </Suspense>
            )}
          </DialogContent>
        </div>
      </div>
    </Dialog>
  );
};

export default DateControlDialog;
